import { type BaseItem, type Specialty } from './common.js';
import {
  type EhrProviderInformation,
  type Kind as EhrProviderKind,
} from './ehrProviderInformation.js';
import { type Setting } from './featureFlag.js';
import { type Organization } from './organization/index.js';
import { type Practice, type SafePractice } from './practice.js';
import { type ProviderRating } from './previsit/provider.js';
import {
  type Preference,
  type ProviderOrganizationPreferenceWithOrgName,
} from './providerOrganizationPreference.js';
import { type Profile, type ProviderProfile } from './providerProfile.js';
import { type PreferenceDetail } from './user/preferences.js';

export enum Role {
  Admin = 'admin',
  Analyst = 'analyst',
  Provider = 'provider',
  Staff = 'staff',
}

export enum Sex {
  Male = 'male',
  Female = 'female',
  Other = 'other',
  Transgender = 'transgender',
}

export enum Kind {
  Active = 'ACTIVE',
  Provisional = 'PROVISIONAL',
}

export type UserDetails =
  | {
      ssoEnabled: false;
      ssoEndpoint: null;
    }
  | {
      ssoEnabled: true;
      ssoEndpoint: string;
    };

export type UserWithDetails = UserWithEhrProviderInformation & {
  userImage: string | null;
  providerProfile: ProviderProfile | null;
  providerOrganizationPreferences: ProviderOrganizationPreferenceWithOrgName[];
  preferences?: PreferenceDetail;
  featureFlags?: Setting[];
};

export interface CreateValues {
  email: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  role?: Role;
  kind: Kind;
  internal: boolean;
}

export interface UpdateValues extends Partial<BaseUser> {}

interface BaseUser {
  role: Role;
  email: string;
  registered: boolean; // whether they've completed user registration
  phoneNumber: string | null;
  phoneNumberVerified: boolean;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  prefix: string | null;
  suffix: string | null;
  sex: Sex | null;
  dateOfBirth: Date | null;
  npi: string | null;
  kind: Kind;
  internal: boolean;
  passbookImageUrl: string | null;
  paypassImageUrl: string | null;
  previsitImageUrl: string | null;
  virtualVisitUrl: string | null;
  deleted: boolean;
  deletedTimestamp: Date | null;
  loginLastAttemptedTimestamp: Date | null;
  loginFailedCount: number;
  loginLocked: boolean;
  careProviderId: string | null;
  enforceInternalIP: boolean;
  primaryPracticeId?: string | null;
}

export interface User extends BaseItem, BaseUser {}

export type UserBasic = Pick<
  User,
  'id' | 'firstName' | 'middleName' | 'lastName' | 'email' | 'registered'
>;

export interface UserWithEhrProviderInformation {
  user: User;
  ehrProviderInformationList: EhrProviderInformation[];
}

export interface UserWithOrganizations extends User {
  organizations: Organization[];
  sessionExpirationDate: string | undefined;
  isImpersonating: boolean;
  hasInternalEmailDomain: boolean;
}
type SafeProviderWithPhone = SafeProvider &
  Pick<User, 'phoneNumber'> & { rating?: ProviderRating | null };
export type UserWithPreferences = SafeProviderWithPhone & {
  preference?: Preference | null;
};
export type UserWithProfile = SafeProviderWithPhone & {
  profile?: Profile | null;
};
export type UserWithProfileAndPreference = SafeProviderWithPhone & {
  profile?: Profile | null;
  preference?: Preference | null;
  practices: SafePractice[];
  id: string;
};

export interface UserIdentification extends Pick<
  BaseUser,
  | 'firstName'
  | 'lastName'
  | 'prefix'
  | 'suffix'
  | 'previsitImageUrl'
  | 'phoneNumber'
  | 'primaryPracticeId'
> {
  specialties?: Specialty[] | null;
  preference?: Pick<Preference, 'providerDirectoryUrl'>;
}

export interface EhrProviderInformationPractice
  extends
    Omit<EhrProviderInformation, 'kind'>,
    Omit<UserIdentification, 'specialties'>,
    Pick<
      Practice,
      'name' | 'address' | 'city' | 'state' | 'zip' | 'timezone' | 'longitude' | 'latitude'
    > {
  specialties: Specialty[] | null;
  phoneNumber: string | null;
  practicePhoneNumber: string;
  practiceFaxNumber: string;
}

export interface CreateUserResponse {
  user: User;
  registrationCode: string;
}

export interface ActivateUserArgs {
  email: string;
  firstName: string;
  lastName: string;
  practiceId: string;
  providerEhrId: string;
}

export interface FindAllOptions {
  q?: string;
  deleted?: 'true' | 'false' | 'all';
  internal?: 'true' | 'false' | 'all';
  role?: Role | 'all';
  excludedRole?: Role;
  kind?: Kind | 'all';
  practiceId?: string;
  practiceGroupId?: string;
  /** filters by users linked with one of the organization's practices */
  practiceOrganizationId?: string;
  /** filters by users linked directly to the organization */
  directOrganizationId?: string;
  page?: number;
  limit?: number;
  id?: string | string[];
}

export interface UserFindAllOptionsPaginated {
  q?: string;
  deleted?: string | boolean;
  internal?: string | boolean;
  role?: string;
  excludedRole?: string;
  kind?: string;
  practiceId?: string;
  practiceGroupId?: string;
  practiceOrganizationId?: string;
  directOrganizationId?: string;
}

export const DOCTOR_PREFIX = 'Dr';

export enum DrSuffixMap {
  AuD = 'AuD',
  DO = 'DO',
  DMD = 'DMD',
  DPM = 'DPM',
  DPT = 'DPT',
  MBBS = 'MBBS',
  MD = 'MD',
  PharmD = 'PharmD',
  PHD = 'PhD',
  PsyD = 'PsyD',
}

export enum OtherSuffixMap {
  ADN = 'ADN',
  AMFT = 'AMFT',
  APRN = 'APRN',
  ASN = 'ASN',
  BSN = 'BSN',
  CNA = 'CNA',
  CNM = 'CNM',
  COTA = 'COTA',
  CPO = 'CPO',
  CRNA = 'CRNA',
  DC = 'DC',
  DNP = 'DNP',
  FNPC = 'FNP-C',
  FNPBC = 'FNP-BC',
  LCSW = 'LCSW',
  LIMHP = 'LIMHP',
  LMFT = 'LMFT',
  LPN = 'LPN',
  LVN = 'LVN',
  MOT = 'MOT',
  MPH = 'MPH',
  MPT = 'MPT',
  MSN = 'MSN',
  MSW = 'MSW',
  NA = 'NA',
  NP = 'NP',
  NPP = 'NP-P',
  OD = 'OD',
  OT = 'OT',
  OTA = 'OTA',
  OTR = 'OTR',
  PA = 'PA',
  PAC = 'PA-C',
  PND = 'PND',
  PRN = 'PRN',
  PT = 'PT',
  PTA = 'PTA',
  RD = 'RD',
  RN = 'RN',
  SLP = 'SLP',
}

export interface ImportRow
  extends
    Pick<
      UpdateValues,
      | 'firstName'
      | 'lastName'
      | 'prefix'
      | 'suffix'
      // | 'dateOfBirth' // not Date
      | 'role'
      | 'registered'
      | 'phoneNumber'
      | 'phoneNumberVerified'
      | 'sex'
      | 'npi'
      | 'kind'
      | 'internal'
      | 'virtualVisitUrl'
      | 'deleted'
    >,
    Pick<PreferenceDetail, 'autoApplyPrevisitFormTemplates'> {
  /**
   * This is in Notable specific date format which requires special handling as described in
   * https://notable-health.atlassian.net/wiki/spaces/notableengineering/pages/3979739157/00.04+Plain+Date+Guidelines
   * Specifically, use our plain date utilities.
   */
  dateOfBirth?: string | null;
  providerEhrId: string;
  practiceId: string;
  imageName?: string;
  email: string;
  ehrProviderKind: EhrProviderKind;
}

/**
 * Payload accepted by `PUT /users`. Narrower than the CSV row: the endpoint cannot apply
 * `dateOfBirth`, `role`, `registered`, `phoneNumberVerified`, `kind` or `deleted`, and takes the
 * three image urls rather than the `imageName` the CSV is verified against.
 */
export interface ImportUpsertValues
  extends
    Pick<
      UpdateValues,
      | 'firstName'
      | 'lastName'
      | 'prefix'
      | 'suffix'
      | 'phoneNumber'
      | 'sex'
      | 'npi'
      | 'internal'
      | 'virtualVisitUrl'
      | 'passbookImageUrl'
      | 'paypassImageUrl'
      | 'previsitImageUrl'
    >,
    Pick<PreferenceDetail, 'autoApplyPrevisitFormTemplates'> {
  email: string;
  practiceId: string;
  providerEhrId: string;
  ehrProviderKind: EhrProviderKind;
}

export const IMPORT_COLUMNS: (keyof ImportRow)[] = [
  'firstName',
  'lastName',
  'prefix',
  'suffix',
  'email',
  'dateOfBirth',
  'practiceId',
  'providerEhrId',
  'role',
  'registered',
  'phoneNumber',
  'phoneNumberVerified',
  'sex',
  'npi',
  'kind',
  'internal',
  'imageName', // This is used to match uploaded pictures to provider row
  'virtualVisitUrl',
  'deleted',
  'autoApplyPrevisitFormTemplates',
  'ehrProviderKind',
];

export type SafeProvider = Pick<
  User,
  | 'firstName'
  | 'lastName'
  | 'prefix'
  | 'previsitImageUrl'
  | 'suffix'
  | 'sex'
  | 'virtualVisitUrl'
  | 'primaryPracticeId'
> & {
  middleName?: string | null;
};

export type TablesAllowedToMerge =
  | 'users'
  | 'ehr_provider_information'
  | 'feature_flags'
  | 'preferences'
  | 'provider_profiles'
  | 'provider_organization_preferences';

export interface MergeUpdate {
  id: string;
  tableName: TablesAllowedToMerge;
  updateValues: Record<string, object>;
}

export interface MergeProviders {
  userToBeDeletedId: string;
  userToBeRetainedId: string;
  mergeUpdates: MergeUpdate[];
}

export interface MergeResponse {
  deletedUserId: string;
  retainedUserId: string;
}
